@file:OptIn(ExperimentalUuidApi::class)

package ai.wagemix.analytics

import ai.wagemix.logic.WMDataGateway
import ai.wagemix.logic.analytics.WMLoginSessionChange
import ai.wagemix.logic.analytics.WMSignInMethod
import ai.wagemix.logic.analytics.signInAttemptFlow
import ai.wagemix.logic.analytics.signedInUidFlow
import com.fsryan.foundation.util.log.FSLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlin.time.Clock
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * Keeps the login session (planning/analytics-plan.md §3) in step with the signed-in user: mints one on sign-in and
 * registration (`login_session_started`, `user_identified`), mints a restored one for a signed-in user who has none,
 * and ends it on sign-out (`signed_out`, before the id is cleared). What to do is decided by
 * [WMLoginSessionChange.of]; this type only observes, stores and reports.
 */
internal interface WMAnalyticsLoginSessionTracker {

    /** Stops observing. */
    fun dispose()
}

internal fun WMAnalyticsLoginSessionTracker(
    dataGateway: WMDataGateway,
    context: WMSiteAnalyticsContext,
    store: WMAnalyticsBrowserStore,
): WMAnalyticsLoginSessionTracker {
    return WMAnalyticsLoginSessionTrackerImpl(dataGateway = dataGateway, context = context, store = store)
}

private class WMAnalyticsLoginSessionTrackerImpl(
    dataGateway: WMDataGateway,
    private val context: WMSiteAnalyticsContext,
    private val store: WMAnalyticsBrowserStore,
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob()),
): WMAnalyticsLoginSessionTracker {

    private var pendingMethod: WMSignInMethod? = null
    private var previousUid: String? = null

    init {
        dataGateway.signInAttemptFlow().onEach { method -> pendingMethod = method }.launchIn(scope)
        dataGateway.signedInUidFlow().onEach { uid -> observe(uid) }.launchIn(scope)
    }

    private fun observe(uid: String?) {
        val now = Clock.System.now()
        val change = WMLoginSessionChange.of(
            stored = store.loginSession(),
            previousUid = previousUid,
            currentUid = uid,
            pendingMethod = pendingMethod,
            now = now,
            newSessionId = { Uuid.random().toString() },
        )
        previousUid = uid
        when (change) {
            WMLoginSessionChange.None -> context.signedInAs(uid = uid, loginSessionId = store.loginSession()?.id)
            is WMLoginSessionChange.Ended -> {
                if (change.signedOut) {
                    FSLog.sendWMSignedOut(loginSessionMinutes = (now - change.session.startedAt).inWholeMinutes)
                }
                store.clearLoginSession()
                context.signedInAs(uid = null, loginSessionId = null)
            }
            is WMLoginSessionChange.Started -> {
                pendingMethod = null
                store.storeLoginSession(change.session)
                context.signedInAs(uid = change.session.uid, loginSessionId = change.session.id)
                change.method?.let { method ->
                    FSLog.sendWMUserIdentified(method = method, isNewUser = method == WMSignInMethod.Register)
                }
                FSLog.sendWMLoginSessionStarted(restored = change.restored)
            }
        }
    }

    override fun dispose() = scope.cancel()
}
